import React from "react";

export interface GridColumn<T> {
  key: keyof T | string;
  label: string;
  render?: (item: T) => React.ReactNode;
  className?: string;
  sortable?: boolean;
}

export interface GridAction<T> {
  label: string;
  icon: React.ReactNode;
  onClick: (item: T) => void;
  disabled?: (item: T) => boolean;
  className?: string;
  title?: string;
}

export interface DataGridProps<T> {
  data: T[];
  // Option 1: Use columns for data-driven cards
  columns?: GridColumn<T>[];
  // Option 2: Use custom render function for cards
  renderCard?: (item: T) => React.ReactNode;
  actions?: GridAction<T>[];
  keyExtractor: (item: T) => string | number;
  containerClassName?: string;
  cardClassName?: string;
  headerClassName?: string;
  rowClassName?: string;
  emptyMessage?: string;
  loading?: boolean;
  onCardClick?: (item: T) => void;
}

export function DataGrid<T>({
  data,
  columns,
  renderCard,
  actions,
  keyExtractor,
  containerClassName = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6",
  cardClassName = "bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden hover:shadow-md transition-all duration-300 transform hover:scale-[1.02]",
  headerClassName = "bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4",
  rowClassName,
  emptyMessage = "Nenhum item encontrado",
  loading = false,
  onCardClick,
}: DataGridProps<T>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando...</span>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      {data.map((item) => (
        <div
          key={keyExtractor(item)}
          className={onCardClick ? "cursor-pointer" : ""}
          onClick={() => onCardClick?.(item)}
        >
          {renderCard ? (
            renderCard(item)
          ) : (
            <div className={`${cardClassName} ${rowClassName || ""}`}>
              {/* Card Header */}
              <div className={headerClassName}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {/* Primary content from first column */}
                    {columns && columns[0] && (
                      <div className="text-white">
                        {columns[0].render
                          ? columns[0].render(item)
                          : String(item[columns[0].key as keyof T] || "")}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {actions && actions.length > 0 && (
                    <div className="flex items-center space-x-1">
                      {actions.map((action, actionIndex) => (
                        <button
                          key={actionIndex}
                          onClick={(e) => {
                            e.stopPropagation();
                            action.onClick(item);
                          }}
                          disabled={action.disabled?.(item)}
                          className={`p-2 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                            action.className ||
                            "text-white/80 hover:text-white hover:bg-white/20"
                          }`}
                          title={action.title || action.label}
                        >
                          {action.icon}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6 space-y-4">
                {columns &&
                  columns.slice(1).map((column, columnIndex) => (
                    <div
                      key={columnIndex}
                      className={`flex items-center justify-between ${
                        column.className || ""
                      }`}
                    >
                      <span className="text-sm font-medium text-gray-500">
                        {column.label}:
                      </span>
                      <div className="text-sm font-semibold text-gray-900">
                        {column.render
                          ? column.render(item)
                          : String(item[column.key as keyof T] || "")}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default DataGrid;
